import { AlertCircle, CloudUpload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useUploadsForJob } from '@/hooks/upload-queue';
import { discard, retry } from '@/lib/upload-queue';

/**
 * What the queue is doing, on the job it is doing it for — ROADMAP.md §6.4.
 *
 * It appears only when there is something to say. A cleaner on good signal
 * never sees it: a photo is queued and uploaded inside a second, and a banner
 * that flashes on every upload is a banner people stop reading.
 *
 * The two states it does show are the two that matter. **Still going** is
 * reassurance — the photos are not lost, do not close the tab. **Stuck** is the
 * one that needs a person: five attempts failed, or the server refused outright,
 * and it says which in the server's own words rather than "something went
 * wrong". Discard asks first, because the photo really is gone afterwards and
 * that means going back to the house.
 */
export function UploadQueueBanner({ jobId }: { jobId: string }) {
  const queued = useUploadsForJob(jobId);
  if (queued.length === 0) return null;

  const working = queued.filter((entry) => !entry.exhausted);
  const stuck = queued.filter((entry) => entry.exhausted);

  return (
    <section
      // A live region: this changes while the cleaner is looking at the
      // checklist rather than at the banner.
      aria-live="polite"
      className="rounded-lg border border-ink-300 bg-white p-4 shadow-card"
    >
      {working.length > 0 ? (
        <p className="flex items-center gap-2 text-body text-ink-900">
          <CloudUpload aria-hidden="true" className="size-4 shrink-0 text-ink-500" />
          {working.length} photo{working.length === 1 ? '' : 's'} uploading. You can carry on — keep
          this page open until they finish.
        </p>
      ) : null}

      {stuck.length > 0 ? (
        <div className={working.length > 0 ? 'mt-3' : ''}>
          <p className="flex items-start gap-2 text-body text-status-priority-text">
            <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            {stuck.length} photo{stuck.length === 1 ? '' : 's'} could not be uploaded.
          </p>

          <ul className="mt-2 space-y-2">
            {stuck.map((entry) => (
              <li key={entry.id} className="rounded-sm border border-ink-300 p-3">
                <p className="text-label text-ink-900">{entry.taskName ?? 'Photo for this job'}</p>
                <p className="mt-0.5 text-meta text-ink-500">{entry.lastError}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      retry(entry.id);
                    }}
                  >
                    Try again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // A window.confirm rather than a dialog component: this is
                      // the one control on the portal that destroys work, and it
                      // should feel heavier than the rest, not lighter.
                      if (window.confirm('Discard this photo? It cannot be recovered.')) {
                        discard(entry.id);
                      }
                    }}
                  >
                    Discard
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
